/**
 * CORE-883 — что уведомление показывает и куда ведёт.
 *
 * Модуль без зависимостей от React Native и от навигации намеренно. Здесь
 * решения: какой значок, как назвать время, какой экран открыть и что делать,
 * когда открывать нечего. Навигация получает уже готовый ответ и только
 * выполняет его — так правила можно проверить тестами, а под jest в этом
 * репозитории компоненты RN не импортируются.
 */
import {
  COMMUNITY_CHAT_NOTIFICATION_TYPES as C,
  MASTER_NOTIFICATION_TYPES as M,
  NOTIFICATION_TARGETS as T,
  PROGRAM_NOTIFICATION_TYPES as P,
  type AppNotification,
} from '../../types/notifications';
import { parseCommunityChatLink } from '../../utils/communityChatLink';
import { eventReportScreen } from '../../services/eventReport';
import { parseLiveChatLink } from '../../utils/liveChatLink';
import { commentIdFromLink, parsePostLink } from '../../utils/postLink';

/** Значок строки. Имена из Ionicons. */
export function notificationIcon(type: string): string {
  switch (type) {
    case P.purchaseConfirmed:
    case P.sale:
      return 'checkmark-circle-outline';
    case P.purchaseFailed:
    case P.salesBroken:
      return 'alert-circle-outline';
    case P.accessExpiring:
      return 'hourglass-outline';
    case P.accessEnded:
      return 'lock-closed-outline';
    case P.canceled:
    case P.liveCanceled:
      return 'close-circle-outline';
    case P.materialAdded:
      return 'add-circle-outline';
    // CORE-68 — новый эфир у мастера, на которого подписан, берёт значок «эфир
    // скоро»: событие одно и то же, разница только в том, когда о нём сказали.
    case P.liveSoon:
    case M.liveCreated:
      return 'radio-outline';
    // CORE-1515 — «начнётся через 15 минут»: про время, а не про новое событие.
    case M.liveStartingSoon:
      return 'alarm-outline';
    // Офлайн отличается от эфира ровно тем, что у него есть место.
    case M.offlineEventCreated:
      return 'location-outline';
    case P.liveMoved:
    case P.scheduleChanged:
      return 'calendar-outline';
    case P.liveRecordingPublished:
    case M.practiceEventCreated:
      return 'play-circle-outline';
    // CORE-1692 — новая публикация для подписчиков.
    case M.postForSubscribers:
      return 'newspaper-outline';
    // CORE-1811 — отклик на публикацию мастера.
    case M.postComment:
      return 'chatbubble-outline';
    case M.postReactions:
      return 'heart-outline';
    case P.refundRequested:
    case P.refundRequestReceived:
    case P.refundApproved:
    case P.refundRejected:
    case P.refundSettled:
      return 'card-outline';
    // CORE-1681 — чаты мастера: упомянули, ответили, эфир завершён и открыт
    // для обсуждения.
    case C.mention:
      return 'at-outline';
    case C.reply:
      return 'arrow-undo-outline';
    case C.liveEnded:
      return 'chatbubbles-outline';
    // CORE-1743 — документы мастера к подписанию.
    case M.documentsToSign:
      return 'document-text-outline';
    // CORE-1563 — подтверждение подписи: договор заключён, согласие дано.
    case M.documentSigned:
      return 'checkmark-circle-outline';
    // CORE-1229 — предложение рассказать, как прошло событие: значок кнопки
    // «Рассказать, как прошло» на странице события.
    case M.eventReportSuggested:
      return 'create-outline';
    default:
      // Незнакомый тип — не повод прятать строку: событие произошло, и человеку
      // о нём сказали. Показываем нейтрально.
      return 'notifications-outline';
  }
}

/**
 * Тон строки. Не цвет: цвета живут в теме и различаются в светлой и тёмной.
 *
 * Тревожным помечено ровно то, где человек теряет деньги или доступ, — иначе
 * тревожный цвет перестаёт значить что-либо.
 */
export type NotificationTone = 'alert' | 'positive' | 'neutral';

export function notificationTone(type: string): NotificationTone {
  switch (type) {
    case P.purchaseFailed:
    case P.canceled:
    case P.liveCanceled:
    case P.refundRejected:
    case P.salesBroken:
      return 'alert';
    case P.purchaseConfirmed:
    case P.sale:
    case P.refundApproved:
    case P.refundSettled:
    case M.documentSigned:
      return 'positive';
    default:
      return 'neutral';
  }
}

// ─── Переход ───

/**
 * Куда вести человека по нажатию.
 *
 * `unavailable` — не ошибка и не заглушка «на будущее»: это уведомление, для
 * которого в текущей сборке действительно нет доступной поверхности. Строка
 * остаётся в ленте, читается и помечается прочитанной, но никуда не ведёт.
 */
export type NotificationRoute =
  | { kind: 'screen'; name: string; params?: Record<string, unknown> }
  | { kind: 'link'; url: string }
  | { kind: 'unavailable'; reason: string };

/**
 * Что маршрутизатору нужно знать о месте, где его спросили.
 *
 * Пока только платформа, и она здесь не из вкуса: событие мастера открывается
 * РАЗНЫМИ экранами — на устройстве это `EventDetail` (роль-зависимый: владельцу
 * правка и «выйти в эфир»), в вебе `LiveDetail`. Так уже устроены лента и
 * профиль мастера (`FeedScreen.handleItemPress`, `useMasterFeed.onOpenEvent`), и
 * уведомление обязано вести туда же, куда карточка того же события.
 *
 * Флагом, а не импортом `Platform`: модуль намеренно ничего не знает про React
 * Native — иначе его не проверить под jest в этом репозитории.
 */
export interface NotificationRouteContext {
  isWeb?: boolean;
  /**
   * CORE-1157 — есть ли на этой платформе поверхность «Программы» и денежные
   * экраны. Значения приходят из `config/flags` (`PROGRAMS_SURFACE_ENABLED` /
   * `MONEY_SURFACE_ENABLED`), а не выводятся здесь из платформы: уведомление
   * обязано вести туда же, куда ведёт интерфейс, и одна правка флага должна
   * менять оба ответа сразу.
   *
   * По умолчанию `true` — модуль отвечает как раньше, если о гейте не сказали.
   */
  programsEnabled?: boolean;
  moneyEnabled?: boolean;
}

function numericId(value: string | null | undefined): number | null {
  if (value == null || value.trim() === '') return null;
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
}

export function resolveNotificationRoute(
  n: AppNotification,
  ctx: NotificationRouteContext = {},
): NotificationRoute {
  const id = numericId(n.targetId);
  const programsEnabled = ctx.programsEnabled !== false;
  const moneyEnabled = ctx.moneyEnabled !== false;

  switch (n.targetType) {
    case T.live: {
      // CORE-68 — страница события. Экран сам решает вопрос доступа: эфир
      // «только для подписчиков» открывается подписчику, и проверять это
      // второй раз здесь незачем — уведомление и так пришло только ему.
      if (id == null) break;
      // CORE-1253 — уведомление о сообщении в чате практики ведёт в сам чат.
      // На устройстве это тоже `LiveDetail`: у `EventDetail` чата нет вовсе.
      const chatLink = parseLiveChatLink(n.deeplink);
      if (chatLink && chatLink.id === id) {
        return { kind: 'screen', name: 'LiveDetail', params: { id, chat: chatLink.chat } };
      }
      return {
        kind: 'screen',
        name: ctx.isWeb ? 'LiveDetail' : 'EventDetail',
        params: { id },
      };
    }

    case T.room: {
      // CORE-1269 — Live-сессия, созданная в визарде. Одна поверхность на
      // обеих платформах (RoomAnnouncementScreen / .native.tsx), поэтому в
      // отличие от `live` здесь нет развилки по `ctx.isWeb` — так же уже
      // ведут CommunityFeed, MyCalendarPanel и остальные места, открывающие
      // комнату по её id.
      if (id == null) break;
      return { kind: 'screen', name: 'RoomAnnouncement', params: { roomId: id } };
    }

    case T.post: {
      // CORE-1692 — публикация по ссылке `/u/{author}/post/{id}`. Экран
      // профиля ведёт на ленту и сам спрашивает пост с проверкой доступа:
      // отписавшемуся — предложение подписаться, удалённый — «недоступна».
      // Автор — из `data.authorUserId` или из ссылки; без него открывать
      // нечего (ленты без автора нет).
      if (id == null) break;
      const link = parsePostLink(n.deeplink);
      const authorUserId = n.data?.authorUserId || n.data?.masterUserId || link?.authorUserId || null;
      if (!authorUserId) break;
      // CORE-1811 — комментарий к посту мастера ведёт к самому комментарию.
      const commentId = Number(n.data?.commentId ?? commentIdFromLink(n.deeplink));
      return Number.isInteger(commentId) && commentId > 0
        ? { kind: 'screen', name: 'PublicUserPost', params: { userId: authorUserId, postId: id, commentId } }
        : { kind: 'screen', name: 'PublicUserPost', params: { userId: authorUserId, postId: id } };
    }

    case T.programLive: {
      // Экран Live-сессии существует и сам проверяет право входа: без покупки
      // он покажет отказ. Это верно — врать о доступе на клиенте не нужно.
      if (id == null) break;
      // CORE-1157 — но эта Live-сессия принадлежит программе, а программ на
      // платформе без них нет. Строка остаётся в ленте и читается: событие
      // произошло, скрывать сам факт незачем.
      if (!programsEnabled) {
        return { kind: 'unavailable', reason: 'Программы доступны в веб-версии' };
      }
      // CORE-879 — отменённой Live-сессии больше нет: отмена удаляет её строку
      // (CORE-988), и экран сессии ответил бы «Live-сессия не найдена». Ведём в
      // программу, которой она принадлежала, — там видно, что сессия снята.
      const programId = numericId(n.data?.programId);
      if (n.type === P.liveCanceled && programId != null) {
        return { kind: 'screen', name: 'ProgramPage', params: { programId } };
      }
      // CORE-883, QA 23.10 — та же сессия могла исчезнуть и после «скоро
      // начнётся», «перенесён» или «запись готова»: отмена снимает только
      // непрочитанные (CORE-992), прочитанные остаются со ссылкой на удалённую
      // строку. Заранее этого не узнать, поэтому программа едет с переходом, и
      // на 404 экран сессии предлагает её вместо тупика «не найдена».
      return {
        kind: 'screen',
        name: 'LiveDetail',
        params: programId != null ? { id, programId } : { id },
      };
    }

    case T.financePurchase:
      // «Финансы → Покупки» (CORE-880) — туда же, куда ведёт пункт «Финансы»
      // в меню (CORE-1135), и сразу с открытым списком. Прежде адрес вёл на
      // «Фонд», который закрыт своим флагом `fund_screen` и на проде показывает
      // «Coming Soon». Конкретную покупку список не раскрывает: подсвечивать
      // одну строку ТЗ не требует.
      // CORE-1157 — без денежных экранов вести некуда: роут не зарегистрирован.
      if (!moneyEnabled) {
        return { kind: 'unavailable', reason: 'Покупки и финансы — в веб-версии' };
      }
      return { kind: 'screen', name: 'Finance', params: { detail: 'purchases' } };

    case T.financeDocuments:
      // CORE-1743 — документы мастера. Экран только свой: id в адресе нет, и
      // чужие документы по уведомлению не открыть.
      if (!moneyEnabled) {
        return { kind: 'unavailable', reason: 'Документы — в веб-версии' };
      }
      return { kind: 'screen', name: 'FinanceDocuments' };

    case T.financeSale:
      // «Финансы → Продажи» (CORE-881): продажа, заявка на возврат и
      // проведённый возврат — это строки отчёта о продажах, а не кошелька.
      // Туда же ведёт кнопка «Открыть продажи» на «Финансах».
      if (!moneyEnabled) {
        return { kind: 'unavailable', reason: 'Продажи и выплаты — в веб-версии' };
      }
      return { kind: 'screen', name: 'MasterSales' };

    case T.masterEvent: {
      // CORE-1229 — «событие закончилось, расскажите, как прошло» открывает
      // композер своей стены в режиме отчёта — ту же форму, что кнопка на
      // странице события (CORE-1230). Удалено ли событие, своё ли оно и нет ли
      // уже отчёта, решает форма, найдя событие: это знание сервера на момент
      // нажатия, а не на момент уведомления. Своего экрана у офлайн-события
      // нет, поэтому другие типы с этой целью идут дальше — к `deeplink`.
      if (id == null || n.type !== M.eventReportSuggested) break;
      const to = eventReportScreen(id);
      return { kind: 'screen', name: to.name, params: to.params };
    }

    case T.programOwner: {
      // Вкладка «Программы» в профиле мастера — она есть (CORE-872).
      if (id == null) break;
      // CORE-1157 — кроме платформ, где вкладки нет.
      if (!programsEnabled) {
        return { kind: 'unavailable', reason: 'Программы доступны в веб-версии' };
      }
      // CORE-883, QA 20.10 — без `programId`. С CORE-880 он значит «покажи
      // купленное»: кабинет прячет авторский раздел и открывает раннер
      // покупателя, и мастер попадал в свою же программу глазами купившего.
      // Открыть конкретную программу в авторском разделе вкладка не умеет —
      // ведём в «Созданное», где она в списке.
      return {
        kind: 'screen',
        name: 'Dashboard',
        params: { initialTab: 'programs' },
      };
    }

    case T.program: {
      // CORE-1279 — прежняя заглушка пережила появление страницы программы и
      // делала карточку некликабельной: при нажатии она только становилась
      // прочитанной. `/programs/:programId` — канонический адрес связанного
      // объекта (тот же адрес присылает backend в `deeplink`), поэтому ведём
      // туда по типизированному target даже если запасной ссылки нет.
      if (!programsEnabled) {
        return { kind: 'unavailable', reason: 'Программы доступны в веб-версии' };
      }
      if (id == null) break;
      // CORE-883, QA 20.10 — новые материалы приходят только купившим, и им
      // нужна сама программа, а не её витрина: с публичной страницы до
      // материала ещё «Продолжить». Ведём в раннер купленного — туда же, куда
      // «Перейти к программе» из «Покупок» (CORE-880). Конкретный модуль не
      // открыть: уведомление сводное, в нём только programId.
      if (n.type === P.materialAdded) {
        return { kind: 'screen', name: 'Dashboard', params: { initialTab: 'programs', programId: id } };
      }
      return { kind: 'screen', name: 'ProgramPage', params: { programId: id } };
    }

    default:
      break;
  }

  // CORE-1253 — ссылка в чат практики остаётся внутри приложения при любом
  // `targetType`: на устройстве `Linking.openURL` увёл бы её в браузер.
  const chatLink = parseLiveChatLink(n.deeplink);
  if (chatLink) {
    return { kind: 'screen', name: 'LiveDetail', params: { id: chatLink.id, chat: chatLink.chat } };
  }

  // CORE-1681 — ссылка в чаты мастера (упоминание, ответ, завершённый эфир).
  // В вебе адрес открывается как есть: дашборд читает обсуждение и сообщение
  // из хэша, и так же ведёт себя клик по push в сервис-воркере. На устройстве
  // `Linking.openURL` увёл бы в браузер — ведём на вкладку чатов профиля.
  const communityLink = parseCommunityChatLink(n.deeplink);
  if (communityLink && !ctx.isWeb) {
    return {
      kind: 'screen',
      name: 'Dashboard',
      params: { viewedUserId: communityLink.masterUserId, initialTab: 'communityChat' },
    };
  }

  // CORE-1692 — ссылка на публикацию при любом `targetType`: на устройстве
  // `Linking.openURL` увёл бы в браузер.
  const postLink = parsePostLink(n.deeplink);
  if (postLink) {
    return { kind: 'screen', name: 'PublicUserPost', params: { userId: postLink.authorUserId, postId: postLink.postId } };
  }

  // Незнакомый адрес — пробуем ссылку: бэк кладёт её в каждое уведомление
  // как раз на этот случай.
  if (n.deeplink) return { kind: 'link', url: n.deeplink };

  return { kind: 'unavailable', reason: 'Открывать нечего' };
}

/** Ведёт ли уведомление хоть куда-нибудь — по этому решается, нажимается ли строка. */
export function isActionable(n: AppNotification, ctx: NotificationRouteContext = {}): boolean {
  return resolveNotificationRoute(n, ctx).kind !== 'unavailable';
}

// ─── Время ───

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Когда это случилось — коротко.
 *
 * В ленте важен порядок и свежесть, а не точная минута: «вчера» человек читает
 * быстрее, чем «21.08.2026, 14:03». Точная дата появляется, когда относительная
 * перестаёт помогать.
 */
export function formatNotificationAge(iso: string, now: Date): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';

  const diff = now.getTime() - at.getTime();
  // Часы на устройстве могут отставать от сервера: уведомление «из будущего» —
  // это не ошибка данных, а расхождение времени, и пугать им незачем.
  if (diff < MINUTE) return 'только что';
  if (diff < HOUR) return `${Math.floor(diff / MINUTE)} мин назад`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)} ч назад`;
  if (diff < 2 * DAY) return 'вчера';
  if (diff < 7 * DAY) return `${Math.floor(diff / DAY)} дн назад`;

  const d = at.getDate().toString().padStart(2, '0');
  const m = (at.getMonth() + 1).toString().padStart(2, '0');
  return `${d}.${m}.${at.getFullYear()}`;
}

export interface NotificationGroup {
  key: string;
  label: string;
  items: AppNotification[];
}

function localDayStamp(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/** Calendar section title in the user's local timezone. */
export function notificationGroupLabel(iso: string, now: Date): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Ранее';

  const dayDiff = Math.round((localDayStamp(now) - localDayStamp(date)) / DAY);
  if (dayDiff === 0) return 'Сегодня';
  if (dayDiff === 1) return 'Вчера';

  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  }).format(date);
}

function notificationGroupKey(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'invalid';
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

/** Keeps the server order while collecting adjacent calendar-day sections. */
export function groupNotifications(items: AppNotification[], now: Date): NotificationGroup[] {
  const groups: NotificationGroup[] = [];
  const byKey = new Map<string, NotificationGroup>();
  for (const item of items) {
    const key = notificationGroupKey(item.createdAtUtc);
    let group = byKey.get(key);
    if (!group) {
      group = { key, label: notificationGroupLabel(item.createdAtUtc, now), items: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.items.push(item);
  }
  return groups;
}

/**
 * Счётчик для бейджа.
 *
 * Свыше 99 показываем «99+»: точное число непрочитанного за сотней не несёт
 * смысла, а разъезжающийся кружок в шапке — несёт, и плохой.
 */
export function formatUnreadBadge(count: number): string {
  if (count <= 0) return '';
  return count > 99 ? '99+' : String(count);
}
